import type React from 'react';
import {useState} from 'react';
import {Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Tooltip} from '@mui/material';
import {RichTextField} from '~/components/MarkdownEditor/MarkdownEditor';
import type {NoteInput} from '../../types/categoryRecordTypes';

type Props = {
    /** Topics for a new note; omit for a reply (a reply has only a body). */
    topics?: string[];
    placeholder: string;
    submitLabel: string;
    onSubmit: (input: NoteInput) => Promise<unknown>;
    onCancel?: () => void;
    autoFocus?: boolean;
};

/** Form for a new note (title, body, topic, Internal only) or a reply (body only). Ctrl/Cmd+Enter posts. */
export const NoteComposer: React.FC<Props> = ({topics, placeholder, submitLabel, onSubmit, onCancel, autoFocus}) => {
    const isNote = topics !== undefined;
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [topic, setTopic] = useState(topics?.[0] ?? '');
    const [internal, setInternal] = useState(false);
    const [busy, setBusy] = useState(false);
    const [round, setRound] = useState(0);  // a new key clears the rich-text field after posting

    const submit = () => {
        if (!body.trim()) return;
        setBusy(true);
        onSubmit({title: title.trim(), body: body.trim(), topic, internal})
            .then(() => {
                setTitle('');
                setBody('');
                setRound(r => r + 1);
                setInternal(false);
                onCancel?.();
            })
            .catch(() => undefined)
            .finally(() => setBusy(false));
    };
    const keys = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
        if (e.key === 'Escape') onCancel?.();
    };

    return (
        <Stack gap={1}>
            {isNote && (
                <TextField size="small" placeholder="Title (optional)" value={title} onChange={e => setTitle(e.target.value)}
                           slotProps={{htmlInput: {maxLength: 200, 'aria-label': 'Note title'}}} onKeyDown={keys}/>
            )}
            <RichTextField key={round} onChange={setBody} placeholder={placeholder} autoFocus={autoFocus}
                           ariaLabel={isNote ? 'Note' : 'Reply'} onSubmit={submit} onCancel={onCancel}/>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                {isNote && (
                    <TextField select size="small" label="Topic" value={topic} onChange={e => setTopic(e.target.value)}
                               sx={{minWidth: 180}}>
                        {topics.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                )}
                {isNote && (
                    <Tooltip title="Only people who can edit this category see it; read-only users do not.">
                        <FormControlLabel label="Internal only" sx={{mr: 0}}
                                          control={<Checkbox size="small" checked={internal}
                                                             onChange={e => setInternal(e.target.checked)}/>}/>
                    </Tooltip>
                )}
                <Button variant="contained" size="small" onClick={submit} disabled={busy || !body.trim() || body.length > 5000}>{submitLabel}</Button>
                {onCancel && <Button size="small" onClick={onCancel} disabled={busy}>Cancel</Button>}
            </Stack>
        </Stack>
    );
};
